import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import {
  replaceEmptyStringWithUndefined,
  replaceNullWithUndefined,
} from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { type FieldError, useForm } from "react-hook-form";
import type { AdminContractCreatePageProps } from "../../model";
import {
  type CreateContractFormSchemaType,
  offerCargoSchema,
} from "../../model/validation";
import { CargoForm } from "./form/cargo-form";
import { DestinationsForm } from "./form/destinations-form";
import { MemoForm } from "./form/memo-form";
import { PaymentForm } from "./form/payment-form";
import { VehicleForm } from "./form/vehicle-form";

export const ContractCreateInProgress = (
  props: AdminContractCreatePageProps,
) => {
  // 初期化フラグ
  const [
    hasDefaultInputValuesInitialized,
    setHasDefaultInputValuesInitialized,
  ] = useState(false);
  const [hasOfferCargoInitialized, setHasOfferCargoInitialized] =
    useState(false);
  const links = useMemo(() => {
    if (props.offerCargoProcess.status !== "success") {
      return undefined;
    }
    const draftOfferCargoId = props.offerCargoProcess.data.draftOfferCargo.id;
    const deliveryProposalOfferCargoId = props.deliveryProposalOfferCargoId;
    return [
      { href: "/admin/cargos", label: "荷物一覧" },
      { href: `/admin/cargos/${draftOfferCargoId}`, label: "荷物詳細" },
      {
        href: `/admin/cargos/${draftOfferCargoId}/delivery-proposals/${deliveryProposalOfferCargoId}`,
        label: "配送提案詳細",
      },
      {
        label: "契約新規作成",
      },
    ];
  }, [
    props.deliveryProposalOfferCargoId,
    props.offerCargoProcess.status,
    props.offerCargoProcess.data,
  ]);

  const deliveryProposalOfferCargo = useMemo(() => {
    if (props.offerCargoProcess.status !== "success") {
      return undefined;
    }
    const deliveryProposal =
      props.offerCargoProcess.data.deliveryProposalOfferCargos.find(
        (offerCargo) => offerCargo.id === props.deliveryProposalOfferCargoId,
      );
    if (!deliveryProposal) {
      throw new Error("Delivery proposal offer cargo not found");
    }
    return deliveryProposal;
  }, [
    props.deliveryProposalOfferCargoId,
    props.offerCargoProcess.status,
    props.offerCargoProcess.data,
  ]);
  const draftOfferCargo = props.offerCargoProcess.data?.draftOfferCargo;
  const { handleSubmit, ...form } = useForm<CreateContractFormSchemaType>({
    resolver: async (values, context, options) => {
      const resolved = await customZodResolver(offerCargoSchema)(
        values,
        context,
        options,
      );
      let shippingFeeError: FieldError | undefined = undefined;
      if (
        deliveryProposalOfferCargo &&
        values.shippingFee &&
        deliveryProposalOfferCargo.shippingFee > values.shippingFee
      ) {
        shippingFeeError = {
          type: "cant_propose_lower_shipping_fee",
          message: "提案された運賃よりも低い価格で契約することはできません。",
        };
      }
      const errors = {
        ...resolved.errors,
        ...(shippingFeeError ? { shippingFee: shippingFeeError } : {}),
      };
      // remove undefined keys
      for (const key in errors) {
        if ((errors as Record<string, unknown>)[key] === undefined) {
          delete (errors as Record<string, unknown>)[key];
        }
      }
      return {
        errors,
        values: resolved.values,
      };
    },
    mode: "onChange",
  });
  // offerCargoProcessのロードを待って、デフォルト値としてセットする（一度だけ実行）
  useLoggingEffect(
    () => {
      // localCacheがある場合の初期化（一度だけ実行）
      if (props.defaultInputValues && !hasDefaultInputValuesInitialized) {
        form.reset(props.defaultInputValues);
        setHasDefaultInputValuesInitialized(true);
        return;
      }

      // 荷物データが取得できた場合の初期化（一度だけ実行）
      if (
        draftOfferCargo &&
        deliveryProposalOfferCargo &&
        !hasOfferCargoInitialized &&
        !props.defaultInputValues
      ) {
        const initialValues = replaceNullWithUndefined({
          ...deliveryProposalOfferCargo,
          companyId: draftOfferCargo.company?.id,
          memo: draftOfferCargo.memo,
          destinations: deliveryProposalOfferCargo.destinations.map(
            (destination, i) => {
              const correspondingDraftDestination =
                draftOfferCargo.destinations[i];
              return {
                ...destination,
                memo: correspondingDraftDestination?.memo,
              };
            },
          ),
        });

        form.reset(initialValues);
        form.trigger();
        setHasOfferCargoInitialized(true);
      }
    },
    [
      draftOfferCargo,
      deliveryProposalOfferCargo,
      form.reset,
      props.defaultInputValues,
      hasDefaultInputValuesInitialized,
      hasOfferCargoInitialized,
    ],
    "ContractCreateInProgress",
  );
  useSubscribeFromValues((values) => {
    if (!deliveryProposalOfferCargo) {
      return;
    }
    props.onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  }, form.watch);

  if (!deliveryProposalOfferCargo || !draftOfferCargo || !props.vehicles.data) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  const hasError = Object.keys(form.formState.errors).length > 0;
  return (
    <Box px={4} my={4} mb={2}>
      <ScrollToTop />
      <BaseBreadCrumb links={links ?? []} isLoading={!links} sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          契約新規作成
        </Typography>
      </Box>
      <Box
        display="flex"
        mx={-4}
        my={2}
        bgcolor="grey.50"
        flexDirection={"column"}
        flexGrow={1}
      >
        <Container maxWidth="md">
          <Stack spacing={3} my={4} mb={10} py={2}>
            <BaseMessageCard
              status={!form.formState.isValid ? "error" : "success"}
              message={
                !form.formState.isValid
                  ? "必須項目が入力されていない箇所があります。契約に必要な情報を入力しましょう。"
                  : "必須項目がすべて入力されています。作成ボタンを押して契約内容を確認しましょう。"
              }
            />
            <DestinationsForm
              {...form}
              deliveryProposalOfferCargo={deliveryProposalOfferCargo}
            />
            <PaymentForm {...form} />
            <CargoForm {...form} />
            <VehicleForm
              {...form}
              deliveryProposalOfferCargo={deliveryProposalOfferCargo}
            />
            <MemoForm
              {...form}
              deliveryProposalOfferCargo={deliveryProposalOfferCargo}
            />
          </Stack>
        </Container>
      </Box>
      <Stack justifyContent="center" alignItems="center" spacing={2}>
        {hasError ? (
          <BaseMessageCard
            status={"error"}
            message={
              "必須項目が入力されていない箇所があります。契約に必要な情報を入力しましょう。"
            }
          />
        ) : null}

        <BaseButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{
            maxWidth: "240px",
          }}
          onClick={handleSubmit((data) => {
            props.onConfirmClick({
              ...data,
              destinations: data.destinations.map((destination) => {
                return {
                  ...destination,
                  // TODO: UI修正までの一時対応。fetch側でaddress分割して送信する
                  city: "",
                  addressLine: undefined,
                };
              }),
              stage: "contract_proposal",
              vehicleId: data.vehicleId ?? null,
              companyId: data.companyId,
            });
          })}
        >
          確認する
        </BaseButton>
      </Stack>
    </Box>
  );
};
